// src/components/WhyInvest.tsx
import React from "react";

type Feature = {
  id: string;
  title: string;
  description: string;
  imgSrc: string;   // ruta relativa a tu carpeta de imágenes
  imgAlt: string;
};

type Props = {
  headline?: string;
  subheadline?: string;
  features?: Feature[];
  footerLine1?: string;
  footerLine2?: string;
};

const defaultFeatures: Feature[] = [
  {
    id: "f1",
    title: "Pioneros en pintura express en 24 horas",
    description: "Somos los primeros en ofrecer este servicio revolucionario.",
    imgSrc: "/images/icons/pioneros.svg",
    imgAlt: "Icono rayo pioneros",
  },
  {
    id: "f2",
    title: "Más de 40.000 servicios realizados",
    description: "Experiencia comprobada y clientes satisfechos.",
    imgSrc: "/images/icons/maservicio.svg",
    imgAlt: "Icono barra servicios",
  },
  {
    id: "f3",
    title: "Imagen oficial de GoDaddy por dos años",
    description: "Reconocidos por tráfico y posicionamiento en Google.",
    imgSrc: "/images/icons/imagenoficial.svg",
    imgAlt: "Icono certificado GoDaddy",
  },
  {
    id: "f4",
    title: "Tecnología propia y marca posicionada",
    description: "Operación eficiente con tecnología desarrollada internamente.",
    imgSrc: "/images/icons/tecnologia.svg",
    imgAlt: "Icono estrella tecnología",
  },
  {
    id: "f5",
    title: "Múltiples fuentes de ingresos",
    description:
      "Gracias a nuestras alianzas y convenios estratégicos a nivel nacional.",
    imgSrc: "/images/icons/Multivers.svg",
    imgAlt: "Icono nodos ingresos",
  },
  {
    id: "f6",
    title: "Retorno de la inversión en tiempo récord",
    description: "Ingresos asegurados desde el primer día.",
    imgSrc: "/images/icons/Retor-Inver.svg",
    imgAlt: "Icono moneda retorno",
  },
];

const WhyInvest: React.FC<Props> = ({
  headline = "¿Por qué invertir en Los Magníficos?",
  subheadline = "Más que una franquicia, somos un modelo de negocio probado y exitoso",
  features = defaultFeatures,
  footerLine1 = "No estás comprando una franquicia.",
  footerLine2 = "Estás comprando un negocio que ya funciona.",
}) => {
  return (
    <section
      aria-label="Razones para invertir"
      className="w-full bg-black text-white py-16 sm:py-20"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        {/* Encabezado */}
        <header className="text-center">
          <h2 className="font-extrabold text-2xl sm:text-3xl md:text-4xl lg:text-5xl" style={{ fontSize: 'clamp(1.5rem, 4vw, 3rem)' }}>
            {headline}
          </h2>
          <p className="text-neutral-300 text-sm sm:text-base md:text-lg lg:text-xl" style={{ fontSize: 'clamp(0.875rem, 2.5vw, 1.5rem)', marginTop: '1.875rem' }}>
            {subheadline}
          </p>
        </header>

        {/* Grid 3x2 */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 justify-items-center">
          {features.map(({ id, title, description, imgSrc, imgAlt }) => (
            <article
              key={id}
              className="rounded-xl bg-neutral-900/40 p-5 sm:p-6 shadow-[0_0_0_1px_rgba(255,255,255,0.03)] w-full max-w-[20rem] sm:max-w-[25rem] md:max-w-[33rem] 2xl:max-w-[45rem] h-[10rem] sm:h-[11rem] md:h-[12.875rem] 2xl:h-[15rem] hover:scale-105 hover:-translate-y-2 transition-all duration-300 ease-in-out cursor-pointer hover:shadow-xl"
              style={{
                border: '0.09375rem solid #F7AC48'
              }}
            >
              <div className="flex items-center justify-center gap-3 sm:gap-4 md:gap-5 h-full">
                {/* Imagen/icono (conexión a tu carpeta) */}
                <div className="shrink-0">
                  <div className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 rounded-md bg-neutral-800/70 grid place-items-center ring-1 ring-white/5">
                    <img
                      src={imgSrc}
                      alt={imgAlt}
                      className="h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 object-contain"
                      loading="lazy"
                    />
                  </div>
                </div>

                <div className="text-left">
                  <h3 className="font-semibold leading-snug text-sm sm:text-base md:text-lg" style={{ color: '#FF9200', fontSize: 'clamp(1rem, 2vw, 1.6rem)' }}>
                    {title}
                  </h3>
                  <p className="mt-1 sm:mt-2 text-neutral-300 leading-relaxed text-xs sm:text-sm md:text-base" style={{ fontSize: 'clamp(0.75rem, 1.8vw, 1.3rem)' }}>
                    {description}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Banda inferior / mensaje */}
        <div className="mt-6 sm:mt-8">
          <div className="rounded-xl bg-neutral-800/80 px-5 sm:px-8 py-6 text-center" style={{ border: '0.09375rem solid #F7AC48' }}>
            <p className="text-neutral-200" style={{ fontSize: '1.3rem' }}>
              {footerLine1}
            </p>
            <p className="mt-1 font-extrabold" style={{ color: '#FF9200', fontSize: '1.6rem' }}>
              {footerLine2}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyInvest;